import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, GoogleLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleSignup() {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const COUNTRIES_ALLOWED = ["USA", "Canada"];
const COUNTRIES_SOON = ["United Kingdom", "Australia", "Germany", "France", "Mexico", "India", "Brazil", "Japan", "Other"];

export default function Register() {
    const [params] = useSearchParams();
    const initialRole = params.get("role") === "advertiser" ? "advertiser" : "respondent";
    const referralCode = params.get("ref") || "";
    const [role, setRole] = useState(initialRole);
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [phone, setPhone] = useState("");
    const [country, setCountry] = useState("USA");
    const [companyName, setCompanyName] = useState("");
    const [consent, setConsent] = useState({ email: false, sms: false, terms: false, privacy: false });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const navigate = useNavigate();
    const { setUser } = useAuth();

    useEffect(() => {
        if (!COUNTRIES_ALLOWED.includes(country)) {
            // soft hint — full block still applies on backend
        }
    }, [country]);

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!COUNTRIES_ALLOWED.includes(country)) {
            navigate("/coming-soon");
            return;
        }
        if (!consent.email || !consent.terms || !consent.privacy) {
            setError("You must accept the Email consent and the Terms of Service & Privacy Policy.");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await api.post("/auth/register", {
                email, password, name, role, country, phone,
                consent,
                referral_code: referralCode || undefined,
                company_name: role === "advertiser" ? companyName : undefined,
            });
            setUser(data.user);
            toast.success("Account created — pending admin approval. Demo accounts pre-approved.");
            navigate(role === "advertiser" ? "/advertiser" : "/respondent", { replace: true });
        } catch (err) {
            setError(errorMessage(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-paper min-h-screen">
            <Navbar />
            <div className="max-w-2xl mx-auto px-6 py-12">
                <p className="eyebrow mb-2">JOIN FREE</p>
                <h1 className="h2 mb-2" data-testid="register-headline">Create your SocietyIntent account.</h1>
                <p className="text-ink-600 mb-8 text-sm">
                    Already a member?{" "}
                    <Link to="/login" className="underline" data-testid="register-to-login-link">Sign in.</Link>
                </p>

                {/* Role switch */}
                <div className="grid grid-cols-2 mb-8 border border-ink-200">
                    <button
                        type="button"
                        onClick={() => setRole("respondent")}
                        className={`py-4 text-left px-5 ${role === "respondent" ? "bg-respondent text-white" : "bg-white"}`}
                        data-testid="role-respondent-btn"
                    >
                        <div className="eyebrow mb-1" style={role === "respondent" ? { color: "rgba(255,255,255,.85)" } : {}}>
                            FOR ME
                        </div>
                        <div className="font-heading text-xl font-bold">Earn cash for surveys</div>
                    </button>
                    <button
                        type="button"
                        onClick={() => setRole("advertiser")}
                        className={`py-4 text-left px-5 border-l border-ink-200 ${role === "advertiser" ? "bg-advertiser text-white" : "bg-white"}`}
                        data-testid="role-advertiser-btn"
                    >
                        <div className="eyebrow mb-1" style={role === "advertiser" ? { color: "rgba(255,255,255,.85)" } : {}}>
                            FOR MY BRAND
                        </div>
                        <div className="font-heading text-xl font-bold">Run paid research</div>
                    </button>
                </div>

                <button
                    type="button"
                    onClick={googleSignup}
                    className="btn btn-outline w-full mb-6"
                    data-testid="google-signup-btn"
                >
                    <GoogleLogo size={18} weight="bold" /> Continue with Google
                </button>

                <div className="flex items-center gap-3 mb-6">
                    <div className="flex-1 border-t border-ink-200" />
                    <span className="font-mono text-[10px] text-ink-600">OR FILL IN</span>
                    <div className="flex-1 border-t border-ink-200" />
                </div>

                <form onSubmit={onSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                            <label className="eyebrow block mb-2">Full name</label>
                            <input className="input-base" required value={name} onChange={(e) => setName(e.target.value)} data-testid="reg-name-input" />
                        </div>
                        <div>
                            <label className="eyebrow block mb-2">Email</label>
                            <input className="input-base" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="reg-email-input" />
                        </div>
                        <div>
                            <label className="eyebrow block mb-2">Password (min 8)</label>
                            <input className="input-base" required type="password" minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="reg-password-input" />
                        </div>
                        <div>
                            <label className="eyebrow block mb-2">Mobile phone (for SMS consent)</label>
                            <input className="input-base" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 010 0000" data-testid="reg-phone-input" />
                        </div>
                        <div>
                            <label className="eyebrow block mb-2">Country</label>
                            <select className="input-base" value={country} onChange={(e) => setCountry(e.target.value)} data-testid="reg-country-select">
                                <optgroup label="Available now">
                                    {COUNTRIES_ALLOWED.map((c) => <option key={c} value={c}>{c}</option>)}
                                </optgroup>
                                <optgroup label="Coming soon">
                                    {COUNTRIES_SOON.map((c) => <option key={c} value={c}>{c} (waitlist)</option>)}
                                </optgroup>
                            </select>
                        </div>
                        {role === "advertiser" && (
                            <div className="sm:col-span-2">
                                <label className="eyebrow block mb-2">Company name</label>
                                <input className="input-base" value={companyName} onChange={(e) => setCompanyName(e.target.value)} data-testid="reg-company-input" />
                            </div>
                        )}
                    </div>

                    {/* Consent block — concise sentence-style with inline clickable links. */}
                    <div className="card-base p-6">
                        <p className="eyebrow mb-4">REQUIRED CONSENT</p>

                        <ConsentRow
                            id="terms-privacy"
                            checked={consent.terms && consent.privacy}
                            onChange={(v) => setConsent((c) => ({ ...c, terms: v, privacy: v }))}
                            data-testid="consent-terms-privacy"
                            required
                        >
                            I agree to the{" "}
                            <Link to="/legal/terms" className="underline" target="_blank" rel="noopener noreferrer" data-testid="consent-terms-link">
                                Terms of Service
                            </Link>
                            {" "}and{" "}
                            <Link to="/legal/privacy" className="underline" target="_blank" rel="noopener noreferrer" data-testid="consent-privacy-link">
                                Privacy Policy
                            </Link>
                            <span className="text-error">*</span>
                        </ConsentRow>

                        <ConsentRow
                            id="email"
                            checked={consent.email}
                            onChange={(v) => setConsent((c) => ({ ...c, email: v }))}
                            data-testid="consent-email"
                            required
                        >
                            By checking this box, I consent to receive transactional emails from SocietyIntent.com related to my account, such as account creation confirmations, survey invitations I qualify for, and payout notifications.
                            <span className="text-error">*</span>
                        </ConsentRow>

                        <ConsentRow
                            id="sms"
                            checked={consent.sms}
                            onChange={(v) => setConsent((c) => ({ ...c, sms: v }))}
                            data-testid="consent-sms"
                        >
                            By checking this box, I agree to receive SMS messages from SocietyIntent.com, including survey-availability alerts and account-security notifications. <span className="text-ink-600">(Optional)</span>
                        </ConsentRow>

                        <p className="legal-box mt-4" data-testid="consent-tcpa-disclaimer">
                            <span className="text-error">*</span> Consent to receive SMS messages is optional and not a condition of using SocietyIntent.com services. Message and data rates may apply. You may receive up to 30 messages per month. You can opt out at any time by texting STOP. For assistance, text HELP or contact us at{" "}
                            <a href="mailto:support@societyintent.com" className="underline">support@societyintent.com</a>
                            . Carriers are not responsible for delayed or undelivered messages. All information collected during the SMS opt-in process will not be sold, rented, or shared. View our{" "}
                            <Link to="/legal/terms" className="underline" target="_blank" rel="noopener noreferrer">Terms of Service</Link>,{" "}
                            <Link to="/legal/privacy" className="underline" target="_blank" rel="noopener noreferrer">Privacy Policy</Link>, and{" "}
                            <Link to="/legal/sms-tcpa" className="underline" target="_blank" rel="noopener noreferrer">SMS Terms</Link>.
                        </p>
                        <p className="font-mono text-[10px] text-ink-600 mt-3">
                            Submitting this form creates a permanent record of your consent including your IP address and timestamp, in compliance with the E-SIGN Act and applicable consumer-protection laws.
                        </p>
                    </div>

                    {error && (
                        <div className="bg-error/10 border border-error/40 text-error text-sm px-4 py-3" data-testid="register-error">
                            {error}
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        className={role === "advertiser" ? "btn btn-advertiser w-full" : "btn btn-respondent w-full"}
                        data-testid="register-submit-btn"
                    >
                        {submitting ? "Creating account…" : (role === "advertiser" ? "Create advertiser account" : "Create my free account")}
                        {!submitting && <ArrowRight size={18} />}
                    </button>
                </form>
            </div>
            <Footer />
        </div>
    );
}

function ConsentRow({ id, checked, onChange, children, required = false, ...rest }) {
    const testid = rest["data-testid"] || `consent-${id}`;
    return (
        <label
            className="flex items-start gap-3 cursor-pointer border-t border-ink-200 first:border-t-0 py-4 first:pt-0"
            data-testid={`${testid}-row`}
        >
            <input
                type="checkbox"
                className="mt-1 w-5 h-5 accent-ink shrink-0"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                required={required}
                aria-required={required || undefined}
                onClick={(e) => e.stopPropagation()}
                data-testid={`${testid}-checkbox`}
            />
            <span
                className="text-sm leading-relaxed"
                onClick={(e) => {
                    // Let inline links navigate normally without toggling the
                    // outer label/checkbox. We don't preventDefault — the link
                    // should still open in a new tab — we just stop the click
                    // from bubbling up to <label>.
                    if (e.target.tagName === "A") e.stopPropagation();
                }}
            >
                {children}
            </span>
        </label>
    );
}
